import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  apiMessage,
  ALL_CREATED_RANGE,
  CREATED_RANGE_OPTIONS,
  createdRangeQuery,
  createdRangeValue,
  createdWindow,
  formatLocalDate,
  getAuditSummary,
  parseLocalDateParam,
  tenantOptionLabel,
} from '../services/jobAuditApi';
import TechnicianScoreGauge from './TechnicianScoreGauge';
import { categoryHeatCell, checklistSections, summaryChecklist, v1WorkOnItems } from './hhrcV2';

const FILTER_CLASS =
  'mt-1 block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm';

const SHADES = ['#e8f4ee', '#f9e7b4', '#f9ca83', '#f4a47d', '#df756f'];

const EMPTY_HEAT = { tenants: [], centers: [], columns: [], categorized: false };

const shadeColor = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return null;
  const index = n < 25 ? 0 : n < 50 ? 1 : n < 75 ? 2 : n < 90 ? 3 : 4;
  return SHADES[index];
};

const formatPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(1);
};

const rangeCaption = (rangeSelect, on) => {
  if (on) return on;
  if (rangeSelect === ALL_CREATED_RANGE) return 'All audits';
  const match = CREATED_RANGE_OPTIONS.find((opt) => opt.value === rangeSelect);
  return match ? match.label : 'Last 7 Days';
};

const centerName = (code, tenants) => {
  const match = (tenants || []).find(
    (tenant) => (typeof tenant === 'string' ? tenant : tenant?.code) === code
  );
  return tenantOptionLabel(match || code) || code;
};

const checklistGroups = (summary) => summaryChecklist(summary?.checklist);

const categoryColumns = (overall, rows) => {
  const columns = [];
  const seen = new Set();
  [checklistGroups(overall), ...rows.map((row) => checklistGroups(row.summary))]
    .flat()
    .forEach((group) => {
      const key = group.category || group.category_label;
      if (!key || seen.has(key)) return;
      seen.add(key);
      columns.push({
        item_id: key,
        issue_label: group.category_label || key,
      });
    });
  return columns;
};

const flatColumns = (overall, rows) => {
  const columns = [];
  const seen = new Set();
  [v1WorkOnItems(overall.work_on), ...rows.map((row) => v1WorkOnItems(row.summary?.work_on))]
    .flat()
    .forEach((item) => {
      if (seen.has(item.item_id)) return;
      seen.add(item.item_id);
      columns.push({
        item_id: item.item_id,
        issue_label: item.issue_label || item.item_id,
      });
    });
  return columns;
};

const toHeat = (overall, rows) => {
  const tenants = overall.tenants || [];
  const categorized =
    checklistGroups(overall).length > 0 ||
    rows.some((row) => checklistGroups(row.summary).length > 0);
  const centers = rows.map((row) => ({
    code: row.tenant_code,
    name: centerName(row.tenant_code, tenants),
    audits: row.audits ?? 0,
    avg_score: row.avg_score,
    cells: categorized
      ? Object.fromEntries(
          checklistGroups(row.summary)
            .map((group) => {
              const key = group.category || group.category_label;
              const cell = categoryHeatCell(group.items);
              return key && cell ? [key, cell] : null;
            })
            .filter(Boolean)
        )
      : Object.fromEntries(
          v1WorkOnItems(row.summary?.work_on).map((item) => [item.item_id, item])
        ),
  }));
  const columns = (categorized ? categoryColumns(overall, rows) : flatColumns(overall, rows)).filter(
    (column) => centers.some((center) => center.cells[column.item_id])
  );
  return { tenants, centers, columns, categorized };
};

const Legend = () => (
  <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-[#172b3c]">
    <b>Finding rate</b>
    {SHADES.map((color, index) => {
      const label = ['0–24%', '25–49%', '50–74%', '75–89%', '90–100%'][index];
      return (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-[13px] w-[22px] rounded-[3px]" style={{ background: color }} />
          {label}
        </span>
      );
    })}
  </div>
);

const RateBar = ({ pct }) => {
  const n = Number(pct);
  const value = Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
  const color = shadeColor(value) || '#e7edf2';
  return (
    <span className="mt-1.5 block h-2 overflow-hidden rounded-full bg-[#e7edf2]" aria-hidden="true">
      <span className="block h-full rounded-full" style={{ width: `${value}%`, background: color }} />
    </span>
  );
};

const CategoryDetails = ({ groups, itemHref }) => {
  const [open, setOpen] = useState({});
  if (!groups.length) return null;

  return (
    <section className="mt-3 overflow-hidden rounded-lg border border-[#dce5ed] bg-white" aria-label="Category Details">
      <div className="border-b border-[#e7edf2] px-4 py-3">
        <h2 className="text-sm font-semibold">Category Details</h2>
        <p className="mt-1 text-xs text-[#5e7184]">
          Each bar is a check&apos;s finding rate. A category shows its highest check until opened.
        </p>
      </div>
      {groups.map((group) => {
        const key = group.category || group.category_label;
        const isOpen = Boolean(open[key]);
        const checks = (group.items || []).filter((item) => item?.item_id);
        const worst = categoryHeatCell(checks);
        const worstPct = worst ? formatPct(worst.fail_pct) : null;
        const name = group.category_label || group.category;
        return (
          <div key={key} className="border-b border-[#e7edf2] last:border-b-0">
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpen((current) => ({ ...current, [key]: !current[key] }))}
              className="w-full px-4 py-3 text-left hover:bg-[#f7fafc]"
            >
              <span className="flex items-center gap-3">
                <span aria-hidden="true" className="w-3 text-xs text-[#5e7184]">
                  {isOpen ? '▾' : '▸'}
                </span>
                <h3 className="min-w-0 flex-1 text-sm font-semibold text-[#172b3c]">{name}</h3>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-[#172b3c]">
                  {worstPct == null ? '—' : `${worstPct}%`}
                </span>
              </span>
              <span className="block pl-6">
                <RateBar pct={worstPct} />
                <span className="mt-1 block text-[11px] text-[#5e7184]">
                  {checks.length} {checks.length === 1 ? 'check' : 'checks'}
                </span>
              </span>
            </button>
            {isOpen ? (
              <div className="space-y-3 px-4 pb-4 pl-10">
                {checklistSections(checks).map((section, index) => (
                  <div key={`${key}-${section.key}-${index}`}>
                    {section.heading ? (
                      <h4 className="text-xs font-semibold text-[#172b3c]">{section.heading}</h4>
                    ) : null}
                    <ul className={section.heading ? 'mt-1' : undefined}>
                      {section.items.map((item) => {
                        const pct = formatPct(item.fail_pct);
                        return (
                          <li key={item.item_id}>
                            <Link
                              to={itemHref(item.item_id)}
                              className="block rounded-md px-2 py-2 hover:bg-[#f7fafc]"
                            >
                              <span className="flex items-baseline justify-between gap-3 text-sm text-[#172b3c]">
                                <span>{item.label}</span>
                                <span className="shrink-0 tabular-nums text-xs text-[#5e7184]">
                                  {pct == null ? '—' : `${pct}%`}
                                  {item.applicable ? ` · ${item.failed}/${item.applicable}` : ''}
                                </span>
                              </span>
                              <RateBar pct={pct} />
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
};

const JobAuditSummary = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';
  const rangeParam = searchParams.get('range') || '';
  const on = searchParams.get('on') || '';
  const rangeSelect = createdRangeValue(rangeParam, on);
  const range = createdRangeQuery(rangeParam, on);
  const onDate = parseLocalDateParam(on);

  const [loading, setLoading] = useState(true);
  const [heat, setHeat] = useState(EMPTY_HEAT);
  const [checklist, setChecklist] = useState([]);

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const workOnHref = (itemId, centerCode) => {
    const params = new URLSearchParams();
    const tenantCode = centerCode || tenant;
    if (tenantCode) params.set('tenant', tenantCode);
    if (on) params.set('on', on);
    else if (range) params.set('range', range);
    else if (rangeSelect === ALL_CREATED_RANGE) params.set('range', ALL_CREATED_RANGE);
    const qs = params.toString();
    const path = `/audit/summary/${encodeURIComponent(itemId)}`;
    return qs ? `${path}?${qs}` : path;
  };

  useEffect(() => {
    let cancelled = false;
    const bounds = createdWindow({ range, on });
    const query = {
      created_after: bounds.created_after || '',
      created_before: bounds.created_before || '',
    };
    setLoading(true);

    (async () => {
      try {
        const overall = await getAuditSummary({ ...query, tenant });
        if (cancelled) return;
        let rows;
        if (tenant) {
          const match = (overall.by_tenant || []).find((row) => row.tenant_code === tenant);
          rows = [
            {
              tenant_code: tenant,
              audits: match?.audits ?? overall.total,
              avg_score: match?.avg_score ?? overall.avg_score,
              summary: overall,
            },
          ];
        } else {
          rows = await Promise.all(
            (overall.by_tenant || [])
              .filter((row) => row?.tenant_code)
              .map(async (row) => ({
                tenant_code: row.tenant_code,
                audits: row.audits,
                avg_score: row.avg_score,
                summary: await getAuditSummary({ ...query, tenant: row.tenant_code }),
              }))
          );
        }
        if (cancelled) return;
        setHeat(toHeat(overall, rows));
        setChecklist(summaryChecklist(overall.checklist));
      } catch (err) {
        if (!cancelled) {
          toast.error(apiMessage(err, 'Failed to load audit summary'));
          setHeat(EMPTY_HEAT);
          setChecklist([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenant, range, on]);

  const saturated = heat.columns.filter((column) => {
    const cells = heat.centers.map((center) => center.cells[column.item_id]);
    return (
      cells.length > 0 &&
      cells.every(
        (cell) => cell && Number(cell.applicable) > 0 && formatPct(cell.fail_pct) === '100.0'
      )
    );
  });

  return (
    <div className="overflow-hidden rounded-xl border border-[#dbe3ea] bg-[#f5f7fa] text-[#172b3c]">
      <header className="border-b border-[#dce5ed] bg-white px-5 py-4">
        <h1 className="text-xl font-semibold">HHRC category findings by center</h1>
        <p className="mt-1 text-xs leading-relaxed text-[#5e7184]">
          {rangeCaption(rangeSelect, on)} ·{' '}
          {heat.categorized
            ? 'Highest finding rate among the checks in each category'
            : 'Percent of applicable audits flagged in each scored item'}{' '}
          · Darker shading means a higher finding rate
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-700">
            Tenant
            <select
              className="mt-1 block w-56 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
              value={tenant}
              onChange={(e) => setFilter({ tenant: e.target.value })}
            >
              <option value="">all</option>
              {(heat.tenants || []).map((option) => (
                <option key={option.code || option} value={option.code || option}>
                  {tenantOptionLabel(option)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Created
            <select
              className={FILTER_CLASS}
              value={rangeSelect}
              onChange={(e) => setFilter({ range: e.target.value, on: '' })}
            >
              <option value={ALL_CREATED_RANGE}>all</option>
              {CREATED_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Date
            <DatePicker
              selected={onDate}
              onChange={(date) => setFilter({ on: date ? formatLocalDate(date) : '', range: '' })}
              dateFormat="yyyy-MM-dd"
              isClearable
              placeholderText="pick a day"
              wrapperClassName="mt-1 block"
              className="block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
            />
          </label>
        </div>
      </header>

      <div className="px-5 py-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="text-sm text-[#5e7184]">Loading summary…</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-[#dce5ed] bg-white">
              <table className="w-full min-w-[1060px] border-separate border-spacing-0 text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-20 min-w-[165px] border-b border-[#e7edf2] bg-[#edf3f8] px-3.5 py-2.5 text-left align-bottom text-[11px] font-bold leading-tight">
                      Center
                    </th>
                    <th className="border-b border-[#e7edf2] bg-[#edf3f8] px-2 py-2.5 text-center align-bottom text-[11px] font-bold leading-tight">
                      Audits
                    </th>
                    <th className="border-b border-[#e7edf2] bg-[#edf3f8] px-2 py-2.5 text-center align-bottom text-[11px] font-bold leading-tight">
                      <span className="inline-flex items-end justify-center gap-1">
                        <span>
                          Average
                          <br />
                          score / 100
                        </span>
                        <span className="group relative mb-px inline-flex">
                          <button
                            type="button"
                            className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-[#5e7184] text-[9px] font-bold leading-none text-[#5e7184]"
                            aria-describedby="average-score-help"
                          >
                            <span aria-hidden="true">?</span>
                            <span className="sr-only">What average score means</span>
                          </button>
                          <span
                            id="average-score-help"
                            role="tooltip"
                            className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 hidden w-52 -translate-x-1/2 whitespace-normal rounded-md bg-[#172b3c] px-2 py-1.5 text-left text-[11px] font-normal leading-snug text-white shadow group-hover:block group-focus-within:block"
                          >
                            Each center&apos;s average is its overall HHRC score out of 100. It is
                            not the average of the finding rates in that row.
                          </span>
                        </span>
                      </span>
                    </th>
                    {heat.columns.map((column) => (
                      <th
                        key={column.item_id}
                        className="max-w-[5.5rem] border-b border-[#e7edf2] bg-[#edf3f8] px-1.5 py-2.5 text-center align-bottom text-[11px] font-bold leading-tight"
                      >
                        {column.issue_label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heat.centers.map((center) => (
                    <tr key={center.code} className="last:[&>td]:border-b-0">
                      <td className="sticky left-0 z-10 min-w-[165px] border-b border-[#e7edf2] bg-white px-3.5 py-2.5 text-left">
                        <button
                          type="button"
                          className="text-left font-bold text-[#172b3c] hover:underline"
                          onClick={() => setFilter({ tenant: center.code })}
                        >
                          {center.name}
                        </button>
                      </td>
                      <td className="border-b border-[#e7edf2] px-2 py-2.5 text-center font-bold">
                        {center.audits}
                      </td>
                      <td className="border-b border-[#e7edf2] px-2 py-2 text-center">
                        <div className="flex justify-center">
                          <TechnicianScoreGauge
                            score={center.avg_score}
                            labelPrefix={`${center.name} average score`}
                          />
                        </div>
                      </td>
                      {heat.columns.map((column) => {
                        const cell = center.cells[column.item_id];
                        const pct = cell ? formatPct(cell.fail_pct) : null;
                        const background = pct == null ? null : shadeColor(pct);
                        if (pct == null || background == null) {
                          return (
                            <td
                              key={column.item_id}
                              className="border-b border-[#e7edf2] px-1.5 py-2 text-center text-[11px] font-normal text-[#64788a]"
                            >
                              —
                            </td>
                          );
                        }
                        return (
                          <td key={column.item_id} className="border-b border-[#e7edf2] px-1.5 py-1.5 text-center">
                            <Link
                              to={workOnHref(cell.item_id, center.code)}
                              className="block w-full min-w-[68px] rounded px-1 py-2.5 font-bold text-[#252a2d] hover:opacity-80"
                              style={{ background }}
                              aria-label={`${center.name}, ${
                                cell.label && cell.label !== column.issue_label
                                  ? `${column.issue_label}, ${cell.label}`
                                  : column.issue_label
                              }, ${pct} percent, ${cell.failed} of ${cell.applicable}`}
                            >
                              {pct}%
                            </Link>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {heat.centers.length === 0 && checklist.length === 0 && (
                    <tr>
                      <td
                        colSpan={3 + heat.columns.length}
                        className="px-4 py-8 text-center text-sm text-[#5e7184]"
                      >
                        No scored audit runs match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Legend />

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-[#dce5ed] bg-white p-4">
                <h2 className="text-sm font-semibold">How to read the table</h2>
                <p className="mt-2 text-xs leading-relaxed text-[#5e7184]">
                  {heat.categorized
                    ? 'Each percentage is the highest finding rate among the checks in that category. Select a cell to open the check behind that rate for the center.'
                    : 'Each percentage is the number of audits with a finding divided by audits where that item was applicable. Select a cell to open that category for the center.'}
                </p>
              </div>
              <div className="rounded-lg border border-[#dce5ed] bg-white p-4">
                <h2 className="text-sm font-semibold">Which items appear</h2>
                <p className="mt-2 text-xs leading-relaxed text-[#5e7184]">
                  {heat.categorized
                    ? "Columns are the top-level checklist categories that have a flagged check in this window, in form order. A dash means that center's summary did not include the category."
                    : "Columns are checklist items with at least 5 applicable audits in this window, ordered by how often they fail. A dash means that center's summary did not include the item."}
                </p>
              </div>
            </div>

            <div className="mt-3 rounded-md bg-[#eaf2f9] p-3 text-xs leading-relaxed text-[#172b3c]">
              {saturated.length > 0 && (
                <p>
                  {saturated.map((column) => column.issue_label).join(', ')}{' '}
                  {saturated.length === 1 ? 'is' : 'are'} flagged in 100% of applicable audits in
                  every center shown. Check the rule and applicability logic before treating that
                  as a coaching conclusion.
                </p>
              )}
              <p className={saturated.length > 0 ? 'mt-2' : undefined}>
                Average scores are overall HHRC scores; they are not averages of the heat-map
                percentages.
              </p>
            </div>

            <CategoryDetails groups={checklist} itemHref={(itemId) => workOnHref(itemId)} />
          </>
        )}
      </div>
    </div>
  );
};

export default JobAuditSummary;
